import React, { useMemo } from "react";
import { Stack, Typography } from "@mui/material";
import { useFormContext } from "react-hook-form";

import {
  HubButton,
  HubHookFormInput,
  HubHookFormSwitch,
  HubHookFormTagInput,
  HubHookFormWrapper,
} from "../../../components";
import { useStationListWithCache } from "../../../components/StationHookFormSelect";
import {
  useDeleteSubscription,
  useGetPreferences,
  useGetSubscriptions,
  usePostSubscription,
  usePostTestChat,
  usePutPreferences,
} from "../../../hooks";

import {
  FormRow,
  MatrixGroupLabel,
  MatrixHeader,
  MatrixRow,
  SectionLabel,
} from "../components";
import { StyledFormCard } from "../styles";
import {
  NOTIFICATION_CHANNELS,
  NOTIFICATION_MATRIX,
} from "../notificationEvents";

const matrixKey = (sub) =>
  sub.resourceId == null && sub.eventType
    ? `${sub.eventType}::${sub.channel}`
    : null;

const matrixField = (eventType, channel) =>
  `matrix.${eventType.replace(/\./g, "_")}.${channel}`;

const NotificationsTab = () => {
  const { data: prefsData } = useGetPreferences();
  const { data: subsData } = useGetSubscriptions();
  const { mutate: putPrefs, isLoading: isSavingPrefs } = usePutPreferences();
  const { mutate: postSub } = usePostSubscription();
  const { mutate: removeSub } = useDeleteSubscription();
  const { mutate: testChat, isLoading: isTesting } = usePostTestChat();
  const { stations = [] } = useStationListWithCache();

  const prefs = prefsData || {};
  const subs = useMemo(() => subsData || [], [subsData]);

  const matrixRowByEvent = useMemo(() => {
    const byKey = {};
    for (const sub of subs) {
      const key = matrixKey(sub);
      if (key) byKey[key] = sub;
    }
    return byKey;
  }, [subs]);

  const defaults = useMemo(() => {
    const matrix = {};
    for (const group of NOTIFICATION_MATRIX) {
      for (const e of group.events) {
        const safe = e.event.replace(/\./g, "_");
        matrix[safe] = {};
        for (const c of NOTIFICATION_CHANNELS) {
          const existing = matrixRowByEvent[`${e.event}::${c.key}`];
          matrix[safe][c.key] = existing && existing?.enabled === true;
        }
      }
    }
    return {
      inAppEnabled: !!prefs.inAppEnabled,
      emailEnabled: !!prefs.emailEnabled,
      googleChatEnabled: !!prefs.googleChatEnabled,
      googleChatWebhookUrl: prefs.googleChatWebhookUrl ?? "",
      autoWatchEnabled: prefs.autoWatchEnabled !== false,
      watchedStationIds: (prefs.watchedStationIds || [])
        .map((id) => stations.find((s) => s.id === id))
        .filter(Boolean),
      matrix,
    };
  }, [prefs, matrixRowByEvent, stations]);

  const updatePref = (patch) => putPrefs({ ...prefs, ...patch });

  const handleMatrixToggle = (
    eventType,
    channel = "any",
    checked,
    resourceType,
  ) => {
    if (!checked) {
      const existing = matrixRowByEvent[`${eventType}::${channel}`];
      if (existing?.id) {
        removeSub(existing.id);
        return;
      }
    }
    postSub({
      eventType,
      resourceType,
      resourceId: null,
      channel,
      enabled: checked,
    });
  };

  return (
    <HubHookFormWrapper defaultValues={defaults} onSubmit={() => {}}>
      <Stack gap={3} maxWidth={1000} mx="auto" width="100%">
        <Stack direction="row" gap={3} mx="auto" width="100%">
          <StyledFormCard>
            <SectionLabel>Channels</SectionLabel>
            <FormRow
              label="In-app"
              helper="Show notifications in your IPC inbox and the bell menu."
            >
              <HubHookFormSwitch
                name="inAppEnabled"
                disabled={isSavingPrefs}
                onChange={(checked) => updatePref({ inAppEnabled: checked })}
                fullWidth={false}
              />
            </FormRow>
            <FormRow
              label="Email"
              helper="Send to the address on your profile."
            >
              <HubHookFormSwitch
                name="emailEnabled"
                disabled={isSavingPrefs}
                onChange={(checked) => updatePref({ emailEnabled: checked })}
                fullWidth={false}
              />
            </FormRow>
            <Stack gap={1.5} width="100%">
              <FormRow
                label="Google Chat"
                helper="Use the IPC - ERP app via Google Chat to get notifications"
              >
                <HubHookFormSwitch
                  name="googleChatEnabled"
                  disabled={isSavingPrefs}
                  onChange={(checked) =>
                    updatePref({ googleChatEnabled: checked })
                  }
                  fullWidth={false}
                />
              </FormRow>
            </Stack>
          </StyledFormCard>

          <StyledFormCard>
            <SectionLabel>Watch Settings</SectionLabel>
            <FormRow
              label="Watch what I create"
              helper="Automatically watch orders, work orders, parts, and shipments you create so you're notified when something happens to them. Turn this off to opt-out by default."
            >
              <HubHookFormSwitch
                name="autoWatchEnabled"
                disabled={isSavingPrefs}
                onChange={(checked) =>
                  updatePref({ autoWatchEnabled: checked })
                }
                fullWidth={false}
              />
            </FormRow>
            <FormRow
              label="Watched Stations"
              helper="Get notified whenever any work order arrives at one of these stations."
              column
            >
              <HubHookFormTagInput
                name="watchedStationIds"
                label=""
                placeholder="Add a station…"
                options={stations}
                disabled={isSavingPrefs}
                onChange={(selected = []) =>
                  updatePref({
                    watchedStationIds: selected.map((s) => Number(s.id)),
                  })
                }
              />
            </FormRow>
          </StyledFormCard>
        </Stack>

        <StyledFormCard>
          <SectionLabel>Event notifications</SectionLabel>
          <Typography
            sx={{
              fontSize: 12,
              color: "text.secondary",
              padding: "0 28px 8px",
            }}
          >
            Choose which channels deliver each kind of event. Channels still
            respect the master switches above.
          </Typography>
          <MatrixHeader channels={NOTIFICATION_CHANNELS} />
          {NOTIFICATION_MATRIX.map((group) => (
            <React.Fragment key={group.group}>
              <MatrixGroupLabel>{group.group}</MatrixGroupLabel>
              {group.events.map((e) => (
                <MatrixRow
                  key={e.event}
                  label={e.label}
                  helper={e.helper}
                  channels={NOTIFICATION_CHANNELS}
                  fieldName={(channelKey) => matrixField(e.event, channelKey)}
                  onCellToggle={(channelKey, checked) =>
                    handleMatrixToggle(
                      e.event,
                      channelKey,
                      checked,
                      group.resourceType,
                    )
                  }
                />
              ))}
            </React.Fragment>
          ))}
        </StyledFormCard>
      </Stack>
    </HubHookFormWrapper>
  );
};

const WebhookTestButton = ({ isTesting, onTest }) => {
  const { getValues } = useFormContext();
  return (
    <HubButton
      size="small"
      variant="contained"
      color="neutral"
      label={isTesting ? "Sending…" : "Test"}
      onClick={() => onTest(getValues())}
      disabled={isTesting}
    />
  );
};

export default NotificationsTab;
